import { RiCloseCircleFill, RiInformationLine } from '@remixicon/react';
import {
    type ComponentPropsWithRef,
    type CSSProperties,
    isValidElement,
    type ReactElement,
    type ReactNode,
    type Ref,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { classNames } from '../../internal/classNames';
import { flattenChildren } from '../../internal/flattenChildren';
import { mergeRefs } from '../../internal/mergeRefs';

import Icon, { type IconSource } from '../Icon/Icon';
import IconAction from '../IconAction/IconAction';
import Tooltip from '../Tooltip/Tooltip';

export type ChipSize = 'small' | 'medium' | 'large';
export type ChipVariant = 'filled' | 'outlined' | 'floating';
export type ChipColor =
    | 'grey'
    | 'yellow'
    | 'orange'
    | 'red'
    | 'cranberry'
    | 'pink'
    | 'purple'
    | 'blue'
    | 'green'
    | 'gradient';

type ChipType = 'meta' | 'choice' | 'tag' | 'input';

export type ChipProps = Omit<ComponentPropsWithRef<'div'>, 'color'> & {
    size?: ChipSize;
    variant?: ChipVariant;
    color?: ChipColor;
    icon?: IconSource;
    /** Leading avatar or other visual (e.g. `<Avatar />`). */
    avatar?: ReactNode;
    tooltipText?: string;
    controlSupport?: ReactElement;
    chipType?: ChipType;
    ref?: Ref<HTMLDivElement>;
};

const ICON_SIZE: Record<ChipSize, number> = {
    small: 12,
    medium: 16,
    large: 16,
};

function chipSurfaceStyle(color: ChipColor): CSSProperties {
    if (color === 'gradient') {
        return {
            '--chip-background-color': 'var(--gradient-light)',
            '--chip-text-color': 'var(--text-support-grey)',
            '--chip-icon-color': 'var(--icon-support-grey)',
        } as CSSProperties;
    }
    if (color === 'grey') {
        return {
            '--chip-background-color': 'var(--surface-support-grey-light)',
            '--chip-text-color': 'var(--text-support-grey)',
            '--chip-icon-color': 'var(--icon-support-grey)',
        } as CSSProperties;
    }
    return {
        '--chip-background-color': `var(--surface-support-${color}-light)`,
        '--chip-text-color': `var(--text-support-${color})`,
        '--chip-icon-color': `var(--icon-support-${color}-dark)`,
    } as CSSProperties;
}

function Chip({
    size = 'medium',
    variant = 'filled',
    color = 'grey',
    icon,
    avatar,
    tooltipText,
    controlSupport,
    chipType = 'meta',
    className = '',
    children,
    style,
    ref,
    ...rest
}: ChipProps) {
    const visualSupport = avatar ? (
        <span className='ui-chip__visual'>{avatar}</span>
    ) : icon ? (
        <span className='ui-chip__visual'>
            <Icon
                icon={icon}
                size={ICON_SIZE[size]}
                className='ui-chip__icon'
            />
        </span>
    ) : null;

    const infoControl =
        tooltipText && !controlSupport ? (
            <IconAction
                icon={RiInformationLine}
                size={size === 'small' ? 'Small' : 'Medium'}
                touchTarget='Medium'
                title={tooltipText}
                className='ui-chip__info'
                aria-label={tooltipText}
            />
        ) : null;

    const finalControl = controlSupport ?? infoControl;

    return (
        <div
            {...rest}
            ref={ref}
            style={{ ...chipSurfaceStyle(color), ...style }}
            className={classNames({
                'ui-chip': true,
                [`ui-chip--${size}`]: true,
                [`ui-chip--${variant}`]: true,
                [`ui-chip--type-${chipType}`]: true,
                [`ui-chip--color-${color}`]: true,
                'ui-chip--has-visual': Boolean(visualSupport),
                [className]: Boolean(className),
            })}>
            {visualSupport}
            <span className='ui-chip__content'>{children}</span>
            {finalControl && <span className='ui-chip__control'>{finalControl}</span>}
        </div>
    );
}

Chip.displayName = 'Chip';

export type ChipChoiceProps = Omit<ChipProps, 'variant' | 'chipType' | 'color'> & {
    selected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
};

export function ChipChoice({
    selected = false,
    disabled = false,
    onClick,
    children,
    className = '',
    role = 'button',
    'aria-checked': ariaChecked,
    ...chipProps
}: ChipChoiceProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <div
            role={role}
            tabIndex={disabled ? -1 : 0}
            aria-checked={ariaChecked ?? (role === 'radio' ? selected : undefined)}
            aria-disabled={disabled || undefined}
            className={classNames({
                'ui-chip-choice': true,
                'ui-chip-choice--selected': selected,
                'ui-chip-choice--disabled': disabled,
                [className]: Boolean(className),
            })}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                inputRef.current?.click();
            }}
            onKeyDown={(e) => {
                if (disabled || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                inputRef.current?.click();
            }}>
            <Chip
                {...chipProps}
                chipType='choice'
                variant='outlined'
                color='grey'>
                {children}
                <input
                    ref={inputRef}
                    type='checkbox'
                    checked={selected}
                    disabled={disabled}
                    readOnly
                    tabIndex={-1}
                    onClick={() => onClick?.()}
                />
            </Chip>
        </div>
    );
}

ChipChoice.displayName = 'ChipChoice';

export type ChipChoiceOption = {
    label: string;
    value: string | number;
    icon?: IconSource;
    avatar?: ReactNode;
    disabled?: boolean;
};

export type ChipChoiceGroupProps = {
    options: ChipChoiceOption[];
    value?: string | number;
    onChange?: (option: ChipChoiceOption) => void;
    size?: ChipSize;
    className?: string;
    id?: string;
};

/** Single-select Choice chip group (Figma "Choice Chip group"). */
export function ChipChoiceGroup({
    options,
    value,
    onChange,
    size = 'large',
    className = '',
    id,
}: ChipChoiceGroupProps) {
    return (
        <div
            id={id}
            role='radiogroup'
            className={classNames({
                'ui-chip-choice-group': true,
                [className]: Boolean(className),
            })}>
            {options.map((option) => (
                <ChipChoice
                    key={option.value}
                    size={size}
                    icon={option.icon}
                    avatar={option.avatar}
                    disabled={option.disabled}
                    selected={value === option.value}
                    role='radio'
                    aria-checked={value === option.value}
                    onClick={() => onChange?.(option)}
                    data-test-name={String(option.value)}>
                    {option.label}
                </ChipChoice>
            ))}
        </div>
    );
}

ChipChoiceGroup.displayName = 'ChipChoiceGroup';

export type ChipListAlign = 'left' | 'center' | 'right';

export type ChipListProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    /** Chip, ChipTag or ChipInput siblings. */
    children: ReactNode;
    /** Default `left`. */
    align?: ChipListAlign;
    /** Shows at most this many chips; the rest are counted in the `+N` chip. */
    limit?: number;
    /** Wraps chips onto several lines instead of collapsing the overflow into `+N`. */
    stacked?: boolean;
    /** Surface of the `+N` chip. Default `filled`. */
    counterVariant?: Extract<ChipVariant, 'filled' | 'outlined'>;
};

/** Pixels the `+N` chip may cover a chip before that chip is counted as hidden. */
const COUNTER_OVERLAP_TOLERANCE = 7;

/**
 * `offsetLeft`, `offsetWidth` and `clientWidth` are rounded, so a chip that ends exactly at the
 * list edge can measure 1px past it. Without this, a list sized to its chips (e.g. in a
 * `CardDefault` `chipTag`) would count its last chip as hidden, grow when the `+N` chip appears,
 * fit again, drop the counter and loop forever.
 */
const SUBPIXEL_ROUNDING_TOLERANCE = 1;

const chipLabel = (chip: ReactNode) => {
    if (!isValidElement<{ children?: ReactNode }>(chip)) return '';
    const label = chip.props.children;
    return typeof label === 'string' || typeof label === 'number' ? String(label) : '';
};

/**
 * Chips on one line. Chips beyond `limit`, or that do not fit the width, collapse into a
 * `+N` chip whose tooltip lists them.
 */
export function ChipList({
    children,
    align = 'left',
    limit = 0,
    stacked = false,
    counterVariant = 'filled',
    className = '',
    ref,
    ...rest
}: ChipListProps) {
    const listRef = useRef<HTMLDivElement>(null);
    const counterRef = useRef<HTMLDivElement>(null);
    const [overflowLabels, setOverflowLabels] = useState<string[]>([]);
    const [counterFloating, setCounterFloating] = useState(false);

    const chips = flattenChildren(children);
    const visibleChips = limit > 0 ? chips.slice(0, limit) : chips;
    const limitedLabels = chips.slice(visibleChips.length).map(chipLabel);
    const overflowing = !stacked && overflowLabels.length > 0;
    const hiddenLabels = [...(overflowing ? overflowLabels : []), ...limitedLabels];
    const showCounter = hiddenLabels.length > 0;
    const firstChip = chips[0];
    const counterSize =
        (isValidElement<{ size?: ChipSize }>(firstChip) && firstChip.props.size) || 'small';

    useLayoutEffect(() => {
        const list = listRef.current;
        if (stacked || !list) {
            setOverflowLabels([]);
            setCounterFloating(false);
            return;
        }
        const measure = () => {
            const items = Array.from(list.children).filter(
                (item) => item !== counterRef.current,
            ) as HTMLElement[];
            const hiddenPast = (edge: number) =>
                items.filter((item) => item.offsetLeft + item.offsetWidth > edge);
            const counter = counterRef.current;
            const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
            const chipsWidth = items.reduce((width, item) => width + item.offsetWidth + gap, 0);
            let hidden = hiddenPast(list.clientWidth + SUBPIXEL_ROUNDING_TOLERANCE);
            const floats =
                counter !== null &&
                (hidden.length > 0 || chipsWidth + counter.offsetWidth > list.clientWidth);
            if (floats) {
                hidden = hiddenPast(
                    list.clientWidth - counter.offsetWidth + COUNTER_OVERLAP_TOLERANCE,
                );
            }
            setCounterFloating(floats);
            const labels = hidden.map((item) => item.textContent ?? '');
            setOverflowLabels((current) =>
                current.length === labels.length &&
                current.every((label, index) => label === labels[index])
                    ? current
                    : labels,
            );
        };
        measure();
        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(list);
        return () => resizeObserver.disconnect();
    }, [children, limit, stacked, showCounter, counterFloating]);

    return (
        <div
            {...rest}
            ref={mergeRefs(listRef, ref)}
            className={classNames({
                'ui-chip-list': true,
                [`ui-chip-list--align-${align}`]: true,
                'ui-chip-list--stacked': stacked,
                'ui-chip-list--counter-floating': !stacked && counterFloating,
                [className]: Boolean(className),
            })}
            data-test-component='ui-kit/ChipList'>
            {visibleChips}
            {showCounter && (
                <Tooltip content={hiddenLabels.filter(Boolean).join(' · ')}>
                    <Chip
                        ref={counterRef}
                        size={counterSize}
                        variant={counterVariant}
                        className={classNames({
                            'ui-chip-list__counter': true,
                            'ui-chip-list__counter--floating': !stacked && counterFloating,
                        })}
                        data-test-name='counter'>
                        +{hiddenLabels.length}
                    </Chip>
                </Tooltip>
            )}
        </div>
    );
}

ChipList.displayName = 'ChipList';

export type ChipTagProps = Omit<ChipProps, 'variant' | 'chipType' | 'avatar'> & {
    variant?: 'filled' | 'floating';
    size?: 'small' | 'large';
};

export function ChipTag({
    variant = 'filled',
    size = 'small',
    className = '',
    ...chipProps
}: ChipTagProps) {
    return (
        <Chip
            {...chipProps}
            variant={variant}
            size={size}
            chipType='tag'
            className={classNames({
                'ui-chip-tag': true,
                [className]: Boolean(className),
            })}
        />
    );
}

ChipTag.displayName = 'ChipTag';

export type ChipInputProps = Omit<
    ChipProps,
    'variant' | 'chipType' | 'color' | 'controlSupport'
> & {
    onClose: () => void;
    disabled?: boolean;
};

export function ChipInput({
    onClose,
    children,
    className = '',
    disabled = false,
    ...chipProps
}: ChipInputProps) {
    return (
        <Chip
            {...chipProps}
            variant='filled'
            color='grey'
            chipType='input'
            className={classNames({
                'ui-chip-input': true,
                [className]: Boolean(className),
            })}
            controlSupport={
                <IconAction
                    icon={RiCloseCircleFill}
                    size='Medium'
                    touchTarget='Medium'
                    className='ui-chip__close'
                    aria-label='Remove'
                    disabled={disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (disabled) return;
                        onClose();
                    }}
                />
            }>
            {children}
        </Chip>
    );
}

ChipInput.displayName = 'ChipInput';

export default Chip;
